import * as React from "react";
import clsx from "clsx";
import { BarChart3 } from "lucide-react";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Switch } from "../../../ui/components/Switch";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { getApiErrorMessage } from "../../lib/api/api-error";
import {
  fetchDeliverySettings,
  saveDeliverySettings,
  type NotificationDeliverySettings,
  type ResolvedDelivery,
} from "../../lib/api/notification-delivery";
import { useI18n } from "../../lib/i18n";
import * as s from "./NotificationDeliverySection.style.css";

export type NotificationDeliveryController = {
  dirty: boolean;
  changeCount: number;
  saving: boolean;
  save: () => Promise<void>;
  reset: () => void;
};

export interface NotificationDeliverySectionProps {
  userId: string;
  token: string;
  onStateChange?: (state: NotificationDeliveryController) => void;
  onViewReport?: () => void;
}

const DEFAULTS: NotificationDeliverySettings = {
  paused: false,
  immediateMinSeverity: null,
  summaryIntervalMinutes: 30,
  resolvedDelivery: "summary",
};

const SUMMARY_OPTIONS = [5, 15, 30, 60];

const KEYS: Array<keyof NotificationDeliverySettings> = [
  "paused",
  "immediateMinSeverity",
  "summaryIntervalMinutes",
  "resolvedDelivery",
];

function Row({
  title,
  description,
  children,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className={s.row}>
      <div>
        <div className={s.rowTitle}>{title}</div>
        {description ? <div className={s.rowDescription}>{description}</div> : null}
      </div>
      <div className={s.control}>{children}</div>
    </div>
  );
}

export function NotificationDeliverySection({
  userId,
  token,
  onStateChange,
  onViewReport,
}: NotificationDeliverySectionProps) {
  const { t } = useI18n();
  const [saved, setSaved] = React.useState<NotificationDeliverySettings>(DEFAULTS);
  const [draft, setDraft] = React.useState<NotificationDeliverySettings>(DEFAULTS);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchDeliverySettings(token, userId)
      .then((data) => {
        if (cancelled) return;
        const next = {
          paused: !!data.paused,
          immediateMinSeverity: data.immediateMinSeverity ?? null,
          summaryIntervalMinutes: data.summaryIntervalMinutes ?? 30,
          resolvedDelivery: data.resolvedDelivery ?? "summary",
        };
        setSaved(next);
        setDraft(next);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, t("pro.notifications.delivery.loadError")));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, userId, t]);

  const changeCount = KEYS.filter((key) => saved[key] !== draft[key]).length;

  const save = React.useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await saveDeliverySettings(token, userId, draft);
      setSaved(result);
      setDraft(result);
    } catch (err) {
      setError(getApiErrorMessage(err, t("pro.notifications.delivery.saveError")));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [token, userId, draft, t]);

  const reset = React.useCallback(() => setDraft(saved), [saved]);

  React.useEffect(() => {
    onStateChange?.({ dirty: changeCount > 0, changeCount, saving, save, reset });
  }, [onStateChange, changeCount, saving, save, reset]);

  const update = <K extends keyof NotificationDeliverySettings>(
    key: K,
    value: NotificationDeliverySettings[K],
  ) => setDraft((current) => ({ ...current, [key]: value }));

  const selectClass = (key: keyof NotificationDeliverySettings) =>
    clsx(formStyles.input, saved[key] !== draft[key] && formStyles.inputChanged);

  if (loading) {
    return <AuthTransitionScreen variant="compact" message={t("pro.notifications.delivery.loading")} />;
  }

  return (
    <div className={s.rows}>
      {error ? <Callout variant="error">{error}</Callout> : null}
      <Row
        title={t("pro.notifications.delivery.pause")}
        description={t("pro.notifications.delivery.pauseHint")}
      >
        <Switch
          label={t("pro.notifications.delivery.pause")}
          hideLabel
          checked={draft.paused}
          color="warning"
          onCheckedChange={(value) => update("paused", value)}
        />
      </Row>
      <Row
        title={t("pro.notifications.delivery.immediate")}
        description={t("pro.notifications.delivery.immediateHint")}
      >
        <select
          className={selectClass("immediateMinSeverity")}
          aria-label={t("pro.notifications.delivery.immediate")}
          value={draft.immediateMinSeverity ?? ""}
          onChange={(e) =>
            update("immediateMinSeverity", e.target.value ? Number(e.target.value) : null)
          }
        >
          <option value="">{t("pro.notifications.delivery.immediateAll")}</option>
          <option value="4">{t("pro.notifications.delivery.immediateHigh")}</option>
          <option value="5">{t("pro.notifications.delivery.immediateCritical")}</option>
        </select>
      </Row>
      <Row
        title={t("pro.notifications.delivery.summary")}
        description={t("pro.notifications.delivery.summaryHint")}
      >
        <select
          className={selectClass("summaryIntervalMinutes")}
          aria-label={t("pro.notifications.delivery.summary")}
          value={draft.summaryIntervalMinutes}
          onChange={(e) => update("summaryIntervalMinutes", Number(e.target.value))}
        >
          {SUMMARY_OPTIONS.map((minutes) => (
            <option key={minutes} value={minutes}>
              {t("pro.notifications.delivery.minutes", { count: minutes })}
            </option>
          ))}
        </select>
      </Row>
      <Row
        title={t("pro.notifications.delivery.resolved")}
        description={t("pro.notifications.delivery.resolvedHint")}
      >
        <select
          className={selectClass("resolvedDelivery")}
          aria-label={t("pro.notifications.delivery.resolved")}
          value={draft.resolvedDelivery}
          onChange={(e) => update("resolvedDelivery", e.target.value as ResolvedDelivery)}
        >
          <option value="summary">{t("pro.notifications.delivery.resolvedSummary")}</option>
          <option value="separate">{t("pro.notifications.delivery.resolvedSeparate")}</option>
          <option value="off">{t("pro.notifications.delivery.resolvedOff")}</option>
        </select>
      </Row>
      {onViewReport ? (
        <div className={s.footer}>
          <span className={s.footerNote}>{t("pro.notifications.delivery.footer")}</span>
          <Button
            variant="outline"
            size="sm"
            iconLeft={<BarChart3 size={16} />}
            onClick={onViewReport}
          >
            {t("pro.notifications.report.open")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
